import { useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface MemoryVocabItem {
  term: string;
  last_failed?: string;
  mastered_on?: string;
}

export interface PersonalContextItem {
  fact: string;
  first_mentioned: string;
}

export interface MemoryJson {
  cefr_level: string;
  strengths: string[];
  working_on: string[];
  next_focus: string;
  failed_vocabulary: MemoryVocabItem[];
  mastered_vocabulary: MemoryVocabItem[];
  personal_context: PersonalContextItem[];
}

export interface SessionLog {
  id: string;
  user_id: string;
  mode: string;
  duration_minutes: number;
  topic_summary: string;
  created_at: string;
}

export interface SessionStats {
  /** Current day streak (consecutive days with at least one session) */
  streak: number;
  /** Total minutes practiced this calendar week (Mon–Sun) */
  minutesThisWeek: number;
  /** Total memory nodes (failed + mastered vocab + personal context) */
  memoryCount: number;
  /** Raw session logs for heatmap / trajectory computations */
  logs: SessionLog[];
}

export interface UserProfile {
  id: string;
  display_name?: string;
  target_language: string;
  native_language: string;
  learning_goal: string;
  streak?: number;
}

// ─── Empty-state defaults (new user with zero history) ───────────────────────

const EMPTY_MEMORY: MemoryJson = {
  cefr_level: 'A1',
  strengths: [],
  working_on: ['Getting started'],
  next_focus: 'Your first conversation with AURA',
  failed_vocabulary: [],
  mastered_vocabulary: [],
  personal_context: [],
};

const EMPTY_STATS: SessionStats = {
  streak: 0,
  minutesThisWeek: 0,
  memoryCount: 0,
  logs: [],
};

// ─── Helper: compute stats from session_logs ──────────────────────────────────

function computeStats(logs: SessionLog[], memoryJson: MemoryJson): SessionStats {
  if (!logs || logs.length === 0) {
    const memoryCount =
      (memoryJson.failed_vocabulary?.length || 0) +
      (memoryJson.mastered_vocabulary?.length || 0) +
      (memoryJson.personal_context?.length || 0);
    return { ...EMPTY_STATS, memoryCount };
  }

  // Sort logs by date desc
  const sorted = [...logs].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );

  // Streak: count consecutive days ending today
  const daySet = new Set(sorted.map((l) => l.created_at.slice(0, 10)));
  let streak = 0;
  const today = new Date();
  for (let i = 0; i < 365; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    const key = d.toISOString().slice(0, 10);
    if (daySet.has(key)) {
      streak++;
    } else if (i > 0) {
      break; // gap — streak broken
    }
  }

  // Minutes this week (Mon–Sun)
  const now = new Date();
  const dayOfWeek = now.getDay(); // 0=Sun
  const daysFromMon = (dayOfWeek + 6) % 7;
  const weekStart = new Date(now);
  weekStart.setDate(now.getDate() - daysFromMon);
  weekStart.setHours(0, 0, 0, 0);
  const minutesThisWeek = logs
    .filter((l) => new Date(l.created_at) >= weekStart)
    .reduce((acc, l) => acc + (l.duration_minutes || 0), 0);

  const memoryCount =
    (memoryJson.failed_vocabulary?.length || 0) +
    (memoryJson.mastered_vocabulary?.length || 0) +
    (memoryJson.personal_context?.length || 0);

  return { streak, minutesThisWeek, memoryCount, logs: sorted };
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export interface DashboardData {
  user: UserProfile | null;
  memory: MemoryJson;
  sessionStats: SessionStats;
  isLoading: boolean;
  /** True when the user has never completed a session */
  isNewUser: boolean;
  /** Re-fetch everything (e.g. after a session ends) */
  refresh: () => void;
}

export function useDashboardData(): DashboardData {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [memory, setMemory] = useState<MemoryJson>(EMPTY_MEMORY);
  const [sessionStats, setSessionStats] = useState<SessionStats>(EMPTY_STATS);
  const [isLoading, setIsLoading] = useState(true);
  const [isNewUser, setIsNewUser] = useState(true);
  const [tick, setTick] = useState(0);

  const refresh = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    let cancelled = false;

    async function fetchAll() {
      setIsLoading(true);
      try {
        // 1. Auth user
        const { data: authData } = await supabase.auth.getUser();
        const userId = authData?.user?.id;
        if (!userId) {
          if (!cancelled) setIsLoading(false);
          return;
        }

        // 2. User profile
        const { data: profileData } = await supabase
          .from('users')
          .select('*')
          .eq('id', userId)
          .maybeSingle();

        // 3. Memory document
        const { data: memDoc } = await supabase
          .from('memory_documents')
          .select('memory_json')
          .eq('user_id', userId)
          .maybeSingle();

        // 4. Session logs (last 90 days, for heatmap + streak)
        const ninetyDaysAgo = new Date();
        ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
        const { data: logsData } = await supabase
          .from('session_logs')
          .select('*')
          .eq('user_id', userId)
          .gte('created_at', ninetyDaysAgo.toISOString())
          .order('created_at', { ascending: false });

        if (cancelled) return;

        const memoryJson: MemoryJson = {
          ...EMPTY_MEMORY,
          ...(memDoc?.memory_json || {}),
        };

        const logs = (logsData as SessionLog[]) || [];
        const stats = computeStats(logs, memoryJson);
        const newUser = logs.length === 0;

        setUser(profileData as UserProfile | null);
        setMemory(memoryJson);
        setSessionStats(stats);
        setIsNewUser(newUser);
      } catch (err) {
        console.error('[useDashboardData] fetch error:', err);
        // On error: keep empty-state defaults — never crash UI
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    fetchAll();
    return () => { cancelled = true; };
  }, [tick]);

  return { user, memory, sessionStats, isLoading, isNewUser, refresh };
}
